import React, { useState } from "react";
import { ICourseSummary, ICourseDocument } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import Input from "src/components/Input/Input";
import TextArea from "src/components/TextArea/TextArea";
import Button from "src/components/Button/Button";

import styles from "./DocumentsTab.module.scss";

interface IProps {
  course: ICourseSummary;
  document?: ICourseDocument;
  onFinish(): void;
  onCancel(): void;
}

const MAX_FILE_SIZE_MB = 50;
const ACCEPTED_TYPES = "image/*,application/pdf";

const DocumentForm: React.FC<IProps> = (props) => {
  const [name, setName] = useState<string>(props.document?.name || "");
  const [description, setDescription] = useState<string>(props.document?.description || "");
  const [file, setFile] = useState<File | undefined>(undefined);
  const [saving, setSaving] = useState<boolean>(false);

  const isEditing = !!props.document;

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) {
      return;
    }

    const isAllowed = selected.type.startsWith("image/") || selected.type === "application/pdf";
    if (!isAllowed) {
      toast.show({
        title: "Add Document",
        content: "Only image and PDF files are supported",
        duration: 8000,
        type: "fail"
      });
      return;
    }

    if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.show({
        title: "Add Document",
        content: `File is too big, please select a file smaller than ${MAX_FILE_SIZE_MB}MB`,
        duration: 8000,
        type: "fail"
      });
      return;
    }

    setFile(selected);
    if (!name) {
      setName(selected.name);
    }
  };

  const onSubmit = async () => {
    if (!name || (!isEditing && !file)) {
      toast.show({
        title: "Add Document",
        content: "Please enter a name and select a file",
        duration: 8000,
        type: "fail"
      });
      return;
    }

    setSaving(true);

    try {
      if (isEditing) {
        await apiService.updateCourseDocument(props.course._id, props.document!._id, { name, description });
      } else {
        const uploadResponse: any = await apiService.uploadFile(file!);

        await apiService.createCourseDocument(props.course._id, {
          name,
          description,
          file_url: uploadResponse.value,
          file_name: file!.name,
          file_size: file!.size,
          mime_type: file!.type,
        });
      }

      toast.show({
        title: "Add Document",
        content: `Document successfully ${isEditing ? "updated" : "added"}`,
        duration: 6000,
        type: "success"
      });

      props.onFinish();
    } catch (e) {
      toast.show({
        title: "Add Document",
        content: "Unable to save document",
        errorDetails: e as any,
        duration: 10000,
        type: "fail"
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); }} className={styles.DocumentForm}>
      <div className={styles.title}>{isEditing ? "Edit Document" : "Add Document"}</div>
      <div className={styles.content}>
        <Input
          label="Document Name"
          placeholder="Enter document name"
          required={true}
          defaultValue={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextArea
          className={styles.descriptionArea}
          label="Description"
          placeholder="Optional description"
          defaultValue={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {!isEditing && (
          <div className={styles.uploadField}>
            <div className={styles.fieldLabel}>Attachment</div>
            <label className={styles.uploadWrap}>
              <input type="file" accept={ACCEPTED_TYPES} onChange={onSelectFile} />
              <div className={styles.uploadInner}>
                <div className={styles.uploadIcon}></div>
                {file ? (
                  <div className={styles.fileName}>{file.name}</div>
                ) : (
                  <>
                    <div className={styles.uploadBtn}>Choose File</div>
                    <div className={styles.uploadHint}>Image or PDF, up to {MAX_FILE_SIZE_MB}MB</div>
                  </>
                )}
              </div>
            </label>
          </div>
        )}
        {isEditing && (
          <div className={styles.fileInfo}>File: {props.document!.file_name}</div>
        )}
      </div>
      <div className={styles.actions}>
        <Button onClick={props.onCancel} variant="secondary" disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" onClick={onSubmit} isLoading={saving}>
          {isEditing ? "Save Document" : "Add Document"}
        </Button>
      </div>
    </form>
  );
};

export default DocumentForm;
